<template>

    <DxValidationGroup ref="validationGroup">
        <div class="d-flex mb-2 gap-2 flex-column">
            <label>{{ $t('signal_viewer.modals.frequency_spectrum_modal.titles.choose_input_signal')
                }}</label>

            <DxSelectBox :items="signalDataSource" :value="0" displayExpr="name" valueExpr="index"
                @selectionChanged="signalChanged" width="300"></DxSelectBox>
        </div>
        <div class="d-flex mb-2 gap-2 flex-column">
            <label>{{ $t('signal_viewer.modals.frequency_spectrum_modal.titles.sampling_frecuency')
                }}</label>
            <DxTextBox :text="samplingFrequency" :disabled="true" width="300"></DxTextBox>
        </div>

        <div class="d-flex align-items-center d-flex gap-2 mb-2">
            <label>{{ $t('signal_viewer.modals.frequency_spectrum_modal.titles.choose_considered_interval')
                }} [</label>

            <DxNumberBox v-model:value="state.intervalStart" :min="0" :max="maxSample" format="fixedPoint" on
                width="100">
                <DxValidator>
                    <DxRangeRule min="0" :max="state.intervalEnd - 1" :reevaluate="true"
                        :message="$t('signal_viewer.modals.frequency_spectrum_modal.dx_number_boxes.interval_start_should_be_less')">
                    </DxRangeRule>
                </DxValidator>
            </DxNumberBox>
            :
            <DxNumberBox v-model:value="state.intervalEnd" :min="0" :max="maxSample" format="fixedPoint"
                @valueChanged="validationGroup?.validate?.()" width="100">

            </DxNumberBox><strong>]</strong>

        </div>
        <div class="d-flex justify-content-start">
            <DxButton :text="$t('signal_viewer.modals.frequency_spectrum_modal.dx_buttons.calculate')"
                @click="calculateSpectrum" styling-mode="contained" type="default"></DxButton>
        </div>
    </DxValidationGroup>

    <div id="signal_spectr" v-once></div>
</template>

<script setup lang="ts">
import { useIntl } from '@/.shared/services/i18n';
import Osc, { type PlotSpectrumOptions } from "./osc";
import { DxRangeRule } from "devextreme-vue/validator";
import type { ClickEvent } from "devextreme/ui/button";
import dxValidationGroup from "devextreme/ui/validation_group";
import { backButtonProps } from "@/.shared/helpers/button-helper";
import type { SelectionChangedEvent } from "devextreme/ui/select_box";
import { useModalBuilder } from "@/.shared/helpers/modal-building-helper";
import { hideLoadingPanel, showLoadingPanel } from "@/.shared/services/loading-service";
import { computed, getCurrentInstance, nextTick, onMounted, reactive, ref, watch } from "vue";
import { DxButton, DxNumberBox, DxSelectBox, DxTextBox, DxValidationGroup, DxValidator } from "devextreme-vue";

const i18n = useIntl();
const modal = useModalBuilder();
const props = defineProps<{}>();
const state: PlotSpectrumOptions = reactive({
    signal: 0,
    intervalStart: 0,
    intervalEnd: 0
});

const validationGroup = ref<dxValidationGroup>();
const data = ref(Osc.getOscData());
const samplingFrequency = computed(() => data.value.samp.toString());
const maxSample = computed(() => data.value.endsamp - 1);


const signalDataSource = computed(() => {
    return (data.value["analog_signals"] as any).map((s: any, idx: number) => ({ name: s.name as string, index: idx }));
})

watch(() => data.value, () => {
    state.intervalEnd = data.value.endsamp - 1;
}, { immediate: true });

onMounted(() => {
    modal.setContext(getCurrentInstance()!);
    modal.addButton("close", { ...backButtonProps(), text: i18n.i18n.t('signal_viewer.modals.frequency_spectrum_modal.buttons.back'), onClick: () => modal.close() }, { toolbar: "bottom", location: "after" });
});

function signalChanged(e: SelectionChangedEvent) {
    state.signal = e.selectedItem.index;
}

async function calculateSpectrum(e: ClickEvent) {
    const validation = (e.validationGroup! as dxValidationGroup).validate();
    if (validation.isValid) {
        const loaderId = showLoadingPanel();
        window.setTimeout(() => {
            Osc.plot_spectr(state);
            hideLoadingPanel(loaderId);
        }, 200);
    }
}
</script>


<style lang="css">
a[href='https://plotly.com/'] {
    display: none;
}
</style>